import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';
import { useLoadMoreScrollRestoration } from 'src/hooks/useLoadMoreScrollRestoration';
import { useScrollToSearchResultsOnActiveFilterChange } from 'src/hooks/useSearchPage';

import ActiveFiltersList from 'components/BaseHelpers/ActiveFiltersList';
import Cta from 'components/BaseHelpers/Cta';
import PeopleGrid from 'components/BaseHelpers/PeopleGrid';
import RichText from 'components/BaseHelpers/RichText/RichText';
import SearchFilterAccordions from 'components/BaseHelpers/SearchFilterAccordions';
import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import TwoColumnLayout from 'components/BaseHelpers/TwoColumnLayout';

import type { ComponentProps } from '../../../types/component-props';
import type { CorePeopleSearchData } from '../../../types/components';
import PeopleAlphaButtons from './components/PeopleAlphaButtons';
import PeopleSearchCombobox from './components/PeopleSearchCombobox';
import { usePeopleSearch } from './usePeopleSearch';

import styles from './CorePeopleSearch.module.scss';

export type CorePeopleSearchProps = ComponentProps & CorePeopleSearchData;

const CorePeopleSearch = (props: CorePeopleSearchProps): JSX.Element => {
  const {
    filters,
    keywordSearchPlaceholder,
    resultsPerPage,
    errorMessage,
    noResultsMessage,
    initialFacetState,
    serviceFilterLabel,
    locationFilterLabel,
    barAdmissionsFilterLabel,
    titlesFilterLabel,
    typeaheadResultsCount,
  } = props.fields;
  const {
    filtersLabel,
    recentSearchesLabel,
    computeResultsCountLabel,
    queryFilterLabel,
    letterFilterLabel,
    clearAllLabel,
  } = useDictionary();

  const search = usePeopleSearch({
    resultsPerPage,
    filters,
    initialFacetState,
  });

  const resultsCount = search.totalCount ?? search.items.length;
  const resultsLabel = computeResultsCountLabel(search.items.length, resultsCount);
  const resultsRef = useScrollToSearchResultsOnActiveFilterChange(search.activeFilters);
  const loadMore = useLoadMoreScrollRestoration({
    itemCount: search.items.length,
    loading: search.loading,
    loadMore: search.loadMore,
  });

  const SearchInput = (
    <PeopleSearchCombobox
      placeholder={keywordSearchPlaceholder}
      queryDraft={search.queryDraft}
      recentSearchesLabel={recentSearchesLabel}
      typeaheadResultsCount={typeaheadResultsCount}
      setQueryDraft={search.setQueryDraft}
      submitQuery={search.submitQuery}
    />
  );

  const AccordionFilters = (
    <SearchFilterAccordions
      filterConfigs={filters.filter((filter) => filter.name !== 'lastnameletter')}
      filterState={search.filters}
      sectionLabel={filtersLabel}
      servicesFilterName="relatedservices"
      facetState={search.facetState}
    />
  );

  const AlphaButtons = (
    <PeopleAlphaButtons
      letterFilter={search.filters.lastnameletter}
      facetState={search.facetState?.lastnameletter}
    />
  );

  return (
    <>
      <StopIndex />
      <section className={cn(styles.main, 'container-12')}>
        {search.activeFilters.length === 0 ? (
          <div className={styles['no-active-filters-layout']}>
            {SearchInput}
            {AlphaButtons}
            {AccordionFilters}
          </div>
        ) : (
          <TwoColumnLayout
            leftChildren={
              <div className={styles['left-column']}>
                {SearchInput}
                <div className={styles['mobile-only']}>{AlphaButtons}</div>
                {AccordionFilters}
              </div>
            }
            rightChildren={
              <div ref={resultsRef} className={styles['right-column']}>
                <div className={cn(styles['results-count'], 'body-sm')}>
                  {search.loading && search.items.length === 0
                    ? 'Loading results...'
                    : resultsLabel}
                </div>
                <ActiveFiltersList
                  labels={{
                    query: queryFilterLabel,
                    lastnameletter: letterFilterLabel,
                    relatedservices: serviceFilterLabel,
                    relatedoffices: locationFilterLabel,
                    baradmissions: barAdmissionsFilterLabel,
                    lawyerlevel: titlesFilterLabel,
                    'clear-all': clearAllLabel,
                  }}
                  activeFilters={search.activeFilters}
                  clearAll={search.clearAll}
                />

                <div className={styles['desktop-only']}>{AlphaButtons}</div>

                {Boolean(search.error) && (
                  <RichText className={cn('body-sm')} field={errorMessage} />
                )}

                {!search.loading && search.items.length === 0 && !search.error && (
                  <RichText className={cn('body-sm')} field={noResultsMessage} />
                )}

                {search.items.length > 0 && (
                  <PeopleGrid className={styles.grid} items={search.items} />
                )}

                {search.hasMore && (
                  <Cta tag="button" onClick={loadMore} disabled={search.loading} type="button">
                    Load more
                  </Cta>
                )}
              </div>
            }
          />
        )}
      </section>
      <StartIndex />
    </>
  );
};

export default CorePeopleSearch;
